import React, { useState, useEffect } from "react";
import cssStyles from "./cms_renderer.module.scss";
import { SectionData, ComponentRenderProps, renderFunctions, renderSection } from "./index";
import { extractKeyStyles } from "./shared";
import { useDeviceType } from "@/hooks/useDeviceType";
import { renderText } from "./index";

interface PanelProps {
    sectionData: SectionData;
    props: ComponentRenderProps;
}

const Panel = ({ sectionData, props }: PanelProps): JSX.Element | null => {
    const panelClassName = "panel";
    const deviceType = useDeviceType();
    const baseStyles = { ...sectionData.uiCompType?.styles };
    const mobileStyles = sectionData.uiCompType?.mStyles;
    const [styles, setStyles] = useState(baseStyles);
    delete sectionData.uiCompType?.styles;

    useEffect(() => {
        if (deviceType === "Mobile" && mobileStyles) {
            setStyles({ ...baseStyles, ...mobileStyles });
        } else {
            setStyles(baseStyles);
        }
    }, [deviceType]);

    const preparePanelContent = () => { return(
        <div id={sectionData.trackId || ('panel_')} className={cssStyles[panelClassName]} style={styles}>
            {Object.keys(sectionData).map((key: string) => {
                const matchedValue = sectionData[key];
                if (key === 'uiCompType' || key === 'link' || key === 'newTab' || key === 'trackInfo' || key === 'trackId') return null;
                const [keyWithExcludedStyles, ...styleParts] = key.split('-');
                const keyWithoutSeqNumber = keyWithExcludedStyles.replace(/\d+$/, '');
                const extracedStyles = extractKeyStyles(styleParts);
                let renderPanelBody = null;
                switch (keyWithoutSeqNumber) {
                    case 'space':
                        renderPanelBody = <div key={key} style={{ height: matchedValue }} />;
                        break;
                    case 'h1':
                        renderPanelBody = renderText(matchedValue, 'title', sectionData.uiCompType, props, 'h1', extracedStyles);
                        break;
                    case 'title':
                        renderPanelBody = renderText(matchedValue, 'title', sectionData.uiCompType, props, 'h2', extracedStyles);
                        break;
                    case 'subTitle':
                        renderPanelBody = renderText(matchedValue, 'subTitle', sectionData.uiCompType, props, 'h3', extracedStyles);
                        break;
                    case 'text':
                        renderPanelBody = renderText(matchedValue, 'text', sectionData.uiCompType, props, 'span', extracedStyles);
                        break;
                    case 'description':
                        renderPanelBody = renderText(matchedValue, 'description', sectionData.uiCompType, props, undefined, extracedStyles);
                        break;
                    case 'imageUrl':
                        const CustImageComponent = renderFunctions.CustImage;
                        renderPanelBody = <CustImageComponent sectionData={{ imageUrl: matchedValue } as SectionData} props={props} />;
                        break;
                    case 'imagePanel':
                        const CustImageComponent2 = renderFunctions.CustImage;
                        renderPanelBody = matchedValue &&
                            <CustImageComponent2 sectionData={{
                                ...matchedValue,
                                uiCompType: {
                                    ...matchedValue.uiCompType,
                                    styles: matchedValue.uiCompType?.styles,
                                    parentStyles: matchedValue.uiCompType?.parentStyles,
                                    containerStyles: matchedValue.uiCompType?.containerStyles
                                }
                            } as SectionData} props={props} />;
                        break;
                    case 'videoUrl':
                        const CustVideoComponent = renderFunctions.CustVideo;
                        renderPanelBody = <CustVideoComponent sectionData={{ videoUrl: matchedValue } as SectionData} props={props} />;
                        break;
                    case 'videoPanel':
                        const CustVideoComponent2 = renderFunctions.CustVideo;
                        renderPanelBody = matchedValue &&
                            <CustVideoComponent2 sectionData={{
                                ...matchedValue,
                                uiCompType: {
                                    ...matchedValue.uiCompType,
                                    styles: matchedValue.uiCompType?.styles,
                                    parentStyles: matchedValue.uiCompType?.parentStyles,
                                    containerStyles: matchedValue.uiCompType?.containerStyles
                                }
                            } as SectionData} props={props} />;
                        break;
                    case 'button':
                        const CustButtonComponent = renderFunctions.CustButton;
                        renderPanelBody = matchedValue && (
                            <div key={key} className={cssStyles[panelClassName + "Button"]} style={matchedValue.uiCompType?.parentStyles}>
                                <CustButtonComponent sectionData={matchedValue as SectionData} props={props} />
                            </div>
                        );
                        break;
                    case 'buttons':
                        const CustButtonComponent2 = renderFunctions.CustButton;
                        const buttons = matchedValue as SectionData[];
                        renderPanelBody = buttons.length > 0 && (
                            <div key={key} className={cssStyles[panelClassName + "Buttons"]} style={buttons[0].uiCompType?.parentStyles}>
                                {buttons.map((button: SectionData, index: number) => (
                                    <CustButtonComponent2 sectionData={button} props={props} index={index} />
                                ))}
                            </div>
                        );
                        break;
                    case 'panels':
                        renderPanelBody = (matchedValue as SectionData[])?.map((item: SectionData, index: number) => (
                            renderSection(item.trackId!, item, props)
                        ));
                        break;
                    case 'rows':
                        renderPanelBody = (matchedValue as SectionData[])?.map((item: SectionData, index: number) => (
                            renderSection(item.trackId!, item, props)
                        ));
                        break;
                    case 'items':
                        renderPanelBody = (matchedValue as SectionData[])?.map((item: SectionData, index: number) => (
                            renderSection(item.trackId!, item, props)
                        ));
                        break;
                    case 'itemsGrid':
                    case 'gridItems':
                        const ItemsGridComponent = renderFunctions.ItemsGrid;
                        const uiCompType = {
                            options: {
                                splitRaito: sectionData.uiCompType?.options?.splitRaito,
                                grid: sectionData.uiCompType?.options?.grid || 'spread',
                                spacing: sectionData.uiCompType?.options?.spacing || 2
                            },
                            styles: sectionData.uiCompType?.parentStyles
                        };
                        const gridSectionData: SectionData = {
                            items: matchedValue as SectionData[],
                            uiCompType: uiCompType
                        };
                        renderPanelBody = <ItemsGridComponent sectionData={gridSectionData} props={props} />;
                        break;
                    default:
                        const otherObject = (sectionData as any)[key] as SectionData;
                        if (typeof otherObject === 'object') {
                            renderPanelBody = renderSection(otherObject.trackId ?? key, otherObject, props);
                        }
                        break;
                }
                return renderPanelBody;
            })}
        </div>
    )};
    return (     
        <>       
            {sectionData.link ? (
            <a href={sectionData.link} target={sectionData.newTab ? "_blank" : "_self"} id={sectionData.trackId || ('panel_')}
                onClick={(e) => {
                    // trackItem(sectionData.trackInfo || { event: 'panelLinkClick', section: sectionData.trackId });
                }}
            >
                {preparePanelContent()}
            </a>
        ) : (preparePanelContent())}
        </>
    )
};
export default Panel;
